import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import ServiceManager from '../../components/ServiceManager';
import BookingInbox from '../../components/BookingInbox';
import { API } from '../../config/api';
import {
  Radio, Users, Eye, Video, Loader2, ExternalLink,
  Inbox, Play, Clock, Wrench, BarChart3, Tv,
  MessageCircle, Share2, TrendingUp
} from 'lucide-react';

export default function StreamerDashboard() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [streams, setStreams] = useState([]);
  const [replays, setReplays] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    if (user?.role && user.role !== 'streamer' && user.role !== 'owner') {
      navigate(`/${user.role}/dashboard`); return;
    }
    fetchData();
  }, [isAuthenticated, authLoading, user]);

  const fetchData = async () => {
    const headers = getAuthHeaders();
    try {
      const [statsRes, streamsRes, replaysRes] = await Promise.all([
        fetch(`${API}/live/streamer-stats`, { headers }),
        fetch(`${API}/live/my-streams`, { headers }),
        fetch(`${API}/live/my-replays`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (streamsRes.ok) { const d = await streamsRes.json(); setStreams(d.streams || []); }
      if (replaysRes.ok) { const d = await replaysRes.json(); setReplays(d.replays || []); }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '';
    const s = parseInt(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="streamer-dashboard">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
              <Radio className="w-6 h-6 text-red-500" />Streamer Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your streams, replays, and analytics</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/u/${user?.handle}`)} data-testid="view-profile-link">
              <ExternalLink className="w-4 h-4 mr-2" />Profile
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => navigate('/live')} data-testid="go-live-cta">
              <Radio className="w-4 h-4 mr-2" />Go Live
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Total Streams', value: stats.total_streams || 0, icon: Video, color: 'text-red-400' },
            { label: 'Total Viewers', value: stats.total_viewers || 0, icon: Users, color: 'text-blue-400' },
            { label: 'Replays', value: stats.replays_count || 0, icon: Play, color: 'text-emerald-400' },
            { label: 'Followers', value: stats.followers || 0, icon: Users, color: 'text-amber-400' },
            { label: 'Replay Views', value: stats.replay_views || 0, icon: Eye, color: 'text-purple-400' },
            { label: 'Chat Messages', value: stats.chat_messages || 0, icon: MessageCircle, color: 'text-green-400' },
            { label: 'Total Gifts', value: stats.total_gifts || 0, icon: TrendingUp, color: 'text-pink-400' },
            { label: 'Live Now', value: stats.live_now || 0, icon: Radio, color: stats.live_now > 0 ? 'text-red-500' : 'text-muted-foreground' },
          ].map(s => (
            <Card key={s.label} className="glass-card border-border p-4">
              <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6 overflow-x-auto">
            <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="dash-tab-overview">
              <BarChart3 className="w-4 h-4" />Overview
            </TabsTrigger>
            <TabsTrigger value="replays" className="flex items-center gap-2" data-testid="dash-tab-replays">
              <Play className="w-4 h-4" />Replays
              {replays.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{replays.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="dash-tab-services">
              <Wrench className="w-4 h-4" />Services
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center gap-2" data-testid="dash-tab-bookings">
              <Inbox className="w-4 h-4" />Bookings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Streams */}
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><Video className="w-5 h-5 text-red-400" />Recent Streams</h3>
                {streams.length === 0 ? (
                  <div className="text-center py-8">
                    <Radio className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-3">No streams yet</p>
                    <Button className="bg-red-600 hover:bg-red-700" size="sm" onClick={() => navigate('/live')}>Start Your First Stream</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {streams.slice(0, 5).map(s => (
                      <div key={s.stream_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-white/[0.07] transition-colors cursor-pointer"
                        onClick={() => s.status === 'live' ? navigate(`/go-live/${s.stream_id}`) : s.replay_id ? navigate(`/live/replay/${s.replay_id}`) : null}>
                        <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                          {s.status === 'live' ? <Radio className="w-5 h-5 text-red-400 animate-pulse" /> : <Video className="w-5 h-5 text-muted-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{s.title}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className={`text-xs ${s.status === 'live' ? 'border-red-500/30 text-red-400' : ''}`}>{s.status}</Badge>
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.peak_viewers || s.viewer_count || 0}</span>
                            {s.category && <Badge variant="secondary" className="text-xs">{s.category}</Badge>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Quick Actions */}
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><Tv className="w-5 h-5 text-red-400" />Quick Actions</h3>
                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start border-red-500/20 hover:bg-red-500/10" onClick={() => navigate('/live')}>
                    <Radio className="w-4 h-4 mr-2" />Go Live Now
                  </Button>
                  <Button variant="outline" className="w-full justify-start border-red-500/20 hover:bg-red-500/10" onClick={() => navigate('/live')}>
                    <Video className="w-4 h-4 mr-2" />Browse Live Streams
                  </Button>
                  <Button variant="outline" className="w-full justify-start border-red-500/20 hover:bg-red-500/10" onClick={() => navigate(`/u/${user?.handle}`)}>
                    <ExternalLink className="w-4 h-4 mr-2" />View Public Profile
                  </Button>
                  <Button variant="outline" className="w-full justify-start border-red-500/20 hover:bg-red-500/10" onClick={() => navigate('/hire-board')}>
                    <Share2 className="w-4 h-4 mr-2" />Browse Hire Board
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Replays Tab */}
          <TabsContent value="replays">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Play className="w-5 h-5" />Your Replays</h3>
            </div>
            {replays.length === 0 ? (
              <Card className="glass-card border-border p-8 text-center">
                <Video className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No replays saved yet. End a stream to save a replay.</p>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {replays.map(r => (
                  <Card key={r.replay_id} className="glass-card border-border overflow-hidden cursor-pointer hover:border-red-500/30 transition-all group"
                    onClick={() => navigate(`/live/replay/${r.replay_id}`)} data-testid={`replay-manage-${r.replay_id}`}>
                    <div className="aspect-video bg-card relative flex items-center justify-center">
                      {r.cover_image || r.thumbnail_url ? (
                        <img src={r.cover_image || r.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Play className="w-8 h-8 text-red-400/50 group-hover:text-red-400 transition-colors" />
                      )}
                      {r.duration_seconds && (
                        <Badge className="absolute bottom-2 right-2 bg-background/70 text-foreground text-xs">
                          <Clock className="w-3 h-3 mr-1" />{formatDuration(r.duration_seconds)}
                        </Badge>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-sm truncate">{r.title}</h4>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{r.views || 0} views</span>
                        <span>{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services"><ServiceManager /></TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings"><BookingInbox /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
